import React from 'react';

const WIDTH = 640;
const HEIGHT = 220;
const PAD = { top: 14, right: 10, bottom: 26, left: 44 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

export const shortDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

export const fullDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

export const shortMonth = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

export const fullMonth = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

// Rounds the axis top up to 1/2/5 x 10^n so gridline labels stay readable.
const niceMax = (max) => {
  if (max <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 2.5, 5, 10].find((s) => max <= s * magnitude) || 10;
  return step * magnitude;
};

const Grid = ({ max, formatValue }) => (
  <g>
    {[0, 0.5, 1].map((fraction) => {
      const y = PAD.top + PLOT_H * (1 - fraction);
      return (
        <g key={fraction}>
          <line x1={PAD.left} y1={y} x2={WIDTH - PAD.right} y2={y} stroke="#e5e7eb" strokeWidth="1" />
          <text x={PAD.left - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="#9ca3af">
            {formatValue(max * fraction)}
          </text>
        </g>
      );
    })}
  </g>
);

const XLabels = ({ data, formatLabel }) => {
  const every = Math.ceil(data.length / 7);
  const slot = PLOT_W / data.length;
  return (
    <g>
      {data.map((point, i) => {
        if (i % every !== 0 && i !== data.length - 1) return null;
        return (
          <text
            key={point.date}
            x={PAD.left + slot * (i + 0.5)}
            y={HEIGHT - 8}
            textAnchor="middle"
            fontSize="10"
            fill="#9ca3af"
          >
            {formatLabel(point.date)}
          </text>
        );
      })}
    </g>
  );
};

const EmptyState = ({ message }) => (
  <div className="h-40 flex items-center justify-center text-sm text-gray-400">{message}</div>
);

export const BarChart = ({
  data = [],
  color = '#db2777',
  formatValue = (v) => Math.round(v),
  unit = '',
  formatLabel = shortDate,
  formatTooltip = fullDate
}) => {
  if (!data.length) return <EmptyState message="No data yet" />;

  const max = niceMax(Math.max(...data.map((d) => d.value), 0));
  const slot = PLOT_W / data.length;
  const barW = Math.max(3, Math.min(34, slot * 0.62));

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-52" role="img">
      <Grid max={max} formatValue={formatValue} />
      {data.map((point, i) => {
        const h = max ? (point.value / max) * PLOT_H : 0;
        return (
          <g key={point.date}>
            <rect
              x={PAD.left + slot * (i + 0.5) - barW / 2}
              y={PAD.top + PLOT_H - h}
              width={barW}
              height={Math.max(h, point.value > 0 ? 2 : 0)}
              rx="2"
              fill={color}
              opacity={point.value ? 1 : 0.25}
            >
              <title>{`${formatTooltip(point.date)}: ${formatValue(point.value)}${unit}`}</title>
            </rect>
          </g>
        );
      })}
      <line x1={PAD.left} y1={PAD.top + PLOT_H} x2={WIDTH - PAD.right} y2={PAD.top + PLOT_H} stroke="#d1d5db" />
      <XLabels data={data} formatLabel={formatLabel} />
    </svg>
  );
};

export const AreaChart = ({
  data = [],
  color = '#801c33',
  formatValue = (v) => Math.round(v),
  unit = '',
  formatLabel = shortDate,
  formatTooltip = fullDate
}) => {
  if (!data.length) return <EmptyState message="No data yet" />;

  const max = niceMax(Math.max(...data.map((d) => d.value), 0));
  const slot = PLOT_W / data.length;
  const pointAt = (i, value) => [
    PAD.left + slot * (i + 0.5),
    PAD.top + PLOT_H - (max ? (value / max) * PLOT_H : 0)
  ];
  const points = data.map((d, i) => pointAt(i, d.value));
  const line = points.map(([x, y]) => `${x},${y}`).join(' ');
  const baseline = PAD.top + PLOT_H;

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-52" role="img">
      <Grid max={max} formatValue={formatValue} />
      <polygon
        points={`${points[0][0]},${baseline} ${line} ${points[points.length - 1][0]},${baseline}`}
        fill={color}
        opacity="0.12"
      />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      {points.map(([x, y], i) => (
        <circle key={data[i].date} cx={x} cy={y} r={data.length > 30 ? 2 : 3} fill="#fff" stroke={color} strokeWidth="1.6">
          <title>{`${formatTooltip(data[i].date)}: ${unit}${formatValue(data[i].value)}`}</title>
        </circle>
      ))}
      <line x1={PAD.left} y1={baseline} x2={WIDTH - PAD.right} y2={baseline} stroke="#d1d5db" />
      <XLabels data={data} formatLabel={formatLabel} />
    </svg>
  );
};

export const BreakdownBar = ({ label, value, total, hint, color = '#db2777' }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-gray-700 truncate">{label}</span>
        <span className="text-gray-500 shrink-0">
          {value.toLocaleString('en-IN')}
          {hint ? <span className="text-gray-400"> · {hint}</span> : null}
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};
